/**
 * Refresh queue — singleton Promise dla `POST /api/auth/refresh` (F7b T7b.1).
 *
 * Problem: gdy tenant dashboard robi 5 równoległych queries z wygasłym access
 * tokenem, każdy request dostanie 401. Bez kolejki każdy wywoła `/refresh` → 5
 * refreshy → 4 błędy `SESSION_REUSED` (po pierwszym udanym rotate reszta ma
 * stary cookie) → wymuszony logout. Atak od samych siebie.
 *
 * Rozwiązanie: singleton `Promise<void>`. Pierwszy 401 inicjuje refresh, kolejne
 * dostają istniejącą promise. Wszyscy czekają, wszyscy retry-ują po sukcesie.
 *
 * Typowy flow:
 *  1. Request A, B, C — każdy dostaje 401 w tym samym event loop.
 *  2. A woła `enqueueRefresh()` → ustawia `refreshPromise` → fetch `/refresh`.
 *  3. B, C wołają `enqueueRefresh()` → dostają istniejącą `refreshPromise`.
 *  4. `/refresh` sukces → `setAccessToken(newToken)` → resolve → A, B, C retry-ują z nowym tokenem.
 *  5. `/refresh` error (np. `SESSION_REUSED`) → reject → A, B, C dostają error.
 *
 * Refresh jest cookie-only (refresh_token w httpOnly cookie), więc tutaj nie przekazujemy
 * argumentów. Endpoint zwraca `{accessToken, expiresIn}` w body.
 */

import type { AxiosInstance } from 'axios';

import { clearAccessToken, setAccessToken } from './access-token';
import { isTenantUpdatingError } from './tenant-updating';

export interface RefreshResponse {
  accessToken: string;
  expiresIn: number;
}

let refreshPromise: Promise<void> | null = null;

/**
 * Enqueue refresh request. Jeśli in-flight, zwraca istniejącą promise.
 * Po sukcesie access token jest zapisany w store (axios interceptor go użyje).
 * Po błędzie token jest wyczyszczony i błąd propagowany do callera.
 */
export function enqueueRefresh(api: AxiosInstance): Promise<void> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = api
    .post<RefreshResponse>('/auth/refresh', undefined, {
      // Flag żeby response interceptor nie próbował retry-ować sam refresha
      // (bez tego: 401 na /refresh → enqueueRefresh → 401 → ∞ loop).
      _skipAuthRefresh: true,
    } as never)
    .then((res) => {
      setAccessToken(res.data.accessToken);
    })
    .catch((err: unknown) => {
      // 503 TENANT_UPDATING (ADR-0113b §5): baza tenanta w aktualizacji, sesja dalej ważna —
      // nie czyścimy tokenu (po dociągnięciu „Odśwież” wraca do pracy bez ponownego logowania).
      if (!isTenantUpdatingError(err)) clearAccessToken();
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

/** Test-only helper — resetuje in-flight promise. */
export function __resetRefreshQueueForTests(): void {
  refreshPromise = null;
}
